<template>
  <div class="row mb-40">
    <div class="col-md-6">
      <div class="form-group mb-25">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <label class="d-block fs-14 text-black mb-0">{{ $t("expense_page.reference_no") }} <span class="text-danger">*</span></label>
        </div>

        <div class="reference-input-wrap">
          <input
            type="text"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            :placeholder="$t('expense_page.reference_no')"
            :value="referenceno"
            @input="$emit('update:referenceno', ($event.target as HTMLInputElement).value)"
          />

          <button
            v-if="!isEditMode"
            type="button"
            class="btn style-two generate-btn"
            @click="$emit('generate-reference')"
          >
            <i class="ri-refresh-line me-1"></i>
            {{ $t("expense_page.generate") }}
          </button>
        </div>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("expense_page.date") }}</label>
        <input
          type="datetime-local"
          class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
          :value="date"
          @input="$emit('update:date', ($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("expense_page.warehouse") }}</label>
        <select
          class="bg-white border-0 rounded-1 fs-14 text-optional"
          :value="form.warehouse_id"
          @change="$emit('update:field', 'warehouse_id', ($event.target as HTMLSelectElement).value)"
        >
          <option value="">{{ $t("expense_page.select_warehouse") }}</option>
          <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
            {{ warehouse.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("expense_page.expense_category") }}</label>
        <div class="reference-input-wrap">
          <select
            class="bg-white border-0 rounded-1 fs-14 text-optional category-select"
            :value="form.category_id"
            @change="$emit('update:field', 'category_id', ($event.target as HTMLSelectElement).value)"
          >
            <option value="">{{ $t("expense_page.select_category") }}</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
          <button
            type="button"
            class="btn style-two generate-btn"
            data-bs-toggle="modal"
            data-bs-target="#expenseCategoryModal"
            @click="$emit('open-category-modal')"
          >
            <i class="ri-add-line me-1"></i>
            {{ $t("expense_page.add_category") }}
          </button>
        </div>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("expense_page.amount") }}</label>
        <input
          type="number"
          class="w-100 d-block shadow-none fs-14 bg-white rounded-1 text-title"
          placeholder="0"
          :value="form.amount"
          @input="$emit('update:field', 'amount', ($event.target as HTMLInputElement).value)"
        />
        <span
          class="percent-sign position-absolute rounded-1 text-center d-flex flex-column justify-content-center fw-semibold text-optional"
        >
          $
        </span>
      </div>
    </div>

    <div class="col-lg-12">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("expense_page.details") }}</label>
        <textarea
          cols="30"
          rows="10"
          :placeholder="$t('expense_page.details_placeholder')"
          class="d-block w-100 bg-white border-0 rounded-1 resize-none fs-14 text-title"
          :value="form.description"
          @input="$emit('update:field', 'description', ($event.target as HTMLTextAreaElement).value)"
        ></textarea>
      </div>
    </div>

    <div class="col-12">
      <button
        class="btn style-one transition border-0 fw-medium text-white rounded-1 fs-md-15 fs-lg-16"
        type="button"
        @click="$emit('submit')"
        :disabled="isLoading"
      >
        {{ isLoading ? (isEditMode ? $t('expense_page.updating') : $t('expense_page.creating')) : (isEditMode ? $t('expense_page.update_expense') : $t('create_expense')) }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { ExpenseCategory } from "../../../services/expense";
import type { Warehouse } from "../../../services/warehouse";

type ExpenseFormState = {
  category_id: string;
  amount: string;
  description: string;
  warehouse_id: string;
};

export default defineComponent({
  name: "CreateExpensesFrom",
  props: {
    referenceno: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    categories: {
      type: Array as PropType<ExpenseCategory[]>,
      default: () => [],
    },
    warehouses: {
      type: Array as PropType<Warehouse[]>,
      default: () => [],
    },
    form: {
      type: Object as PropType<ExpenseFormState>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:referenceno",
    "update:date",
    "update:field",
    "generate-reference",
    "open-category-modal",
    "submit",
  ],
});
</script>

<style scoped>
.reference-input-wrap {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.reference-input-wrap input {
  flex: 1;
}

.reference-input-wrap select {
  flex: 1;
}

.generate-btn {
  min-width: 124px;
  white-space: nowrap;
}
</style>
